import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, User, Users, Edit2, Trash2, Loader } from 'lucide-react';
import { formatWeekday } from '../../utils/localeFormatters';

// Hover styles only apply on pointers that actually hover. On touch devices
// (iOS Safari in particular) JS-driven mouseenter handlers can leave the
// element in a "sticky hover" state and require a second tap for the click
// to fire — hence CSS-only hover gated to hover-capable devices.
const UPCOMING_LESSONS_STYLES = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .upcoming-lesson-action {
    padding: 0.5rem;
    background-color: transparent;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
  @media (hover: hover) {
    .upcoming-lesson-row:hover {
      border-color: var(--color-primary-dark) !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
    }
    .upcoming-lesson-action--edit:hover {
      background-color: var(--color-primary-light);
      border-color: var(--color-primary-dark);
    }
    .upcoming-lesson-action--delete:hover {
      background-color: var(--color-danger-light, #fee2e2);
      border-color: #ef4444;
    }
  }
  .upcoming-lesson-action:active {
    transform: scale(0.97);
  }
`;

/**
 * UpcomingLessonsList - Displays upcoming lessons grouped by date
 * Shows lessons from today to +7 days (Learning centers only)
 */
export default function UpcomingLessonsList({ lessons = [], onEdit, onDelete, isLoading = false }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  const parseDate = (d) => {
    if (d && d._seconds) return new Date(d._seconds * 1000);
    return new Date(d);
  };

  // Group lessons by date
  const groupedLessons = {};
  const sortedLessons = [...lessons].sort((a, b) => {
    const dateA = parseDate(a.scheduledDate || a.date);
    const dateB = parseDate(b.scheduledDate || b.date);
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA - dateB;
    }
    return (a.startTime || '').localeCompare(b.startTime || '');
  });

  sortedLessons.forEach((lesson) => {
    const date = parseDate(lesson.scheduledDate || lesson.date);
    const dateKey = date.toISOString().split('T')[0];
    if (!groupedLessons[dateKey]) {
      groupedLessons[dateKey] = [];
    }
    groupedLessons[dateKey].push(lesson);
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return t('lessons.today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t('lessons.tomorrow');
    } else {
      return formatWeekday(date, i18n.language);
    }
  };

  const getSessionTypeLabel = (type) => {
    const labels = {
      regular: t('lessons.regular'),
      review: t('lessons.review'),
      exam: t('lessons.exam'),
      makeup: t('lessons.makeup')
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          minHeight: '200px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary-dark)' }} />
        </div>
        <style>{UPCOMING_LESSONS_STYLES}</style>
      </div>
    );
  }

  return (
    <div
      data-testid="upcoming-lessons-list"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        minHeight: '200px'
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <BookOpen size={20} color="var(--color-primary-dark)" />
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '600',
          color: 'var(--color-text-primary)',
          margin: 0
        }}>
          {t('dashboard.upcomingLessons')}
        </h3>
        {lessons.length > 0 && (
          <span style={{
            marginLeft: 'auto',
            marginRight: isRTL ? 0 : 'auto',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--color-primary-dark)',
            backgroundColor: 'var(--color-primary-light)',
            padding: '0.25rem 0.625rem',
            borderRadius: '12px'
          }}>
            {lessons.length}
          </span>
        )}
      </div>

      {/* Lessons List */}
      {lessons.length === 0 ? (
        <div
          style={{
            padding: '3rem 1.5rem',
            textAlign: 'center',
            color: 'var(--color-text-secondary)'
          }}
        >
          <BookOpen size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: '500' }}>
            {t('dashboard.noUpcomingLessons')}
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8125rem' }}>
            {t('dashboard.scheduledLessonsPlaceholder')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {Object.keys(groupedLessons).map((dateKey) => (
            <div key={dateKey}>
              {/* Date Header */}
              <div
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                {formatDate(dateKey)}
              </div>

              {/* Lessons for this date */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {groupedLessons[dateKey].map((lesson) => (
                  <div
                    key={lesson.id}
                    className="upcoming-lesson-row"
                    style={{
                      padding: '1rem',
                      backgroundColor: 'var(--color-hover)',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                      {/* Lesson Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Student/Group Name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                          {lesson.groupId ? (
                            <>
                              <Users size={16} color="var(--color-primary-dark)" />
                              <span style={{
                                fontSize: '0.9375rem',
                                fontWeight: '600',
                                color: 'var(--color-text-primary)'
                              }}>
                                {lesson.groupName || t('lessons.group')}
                              </span>
                            </>
                          ) : (
                            <>
                              <User size={16} color="var(--color-primary-dark)" />
                              <span style={{
                                fontSize: '0.9375rem',
                                fontWeight: '600',
                                color: 'var(--color-text-primary)'
                              }}>
                                {lesson.studentName || t('common.student')}
                              </span>
                              <span style={{
                                fontSize: '0.6875rem',
                                fontWeight: '600',
                                color: '#7c3aed',
                                backgroundColor: 'var(--cat-purple-bg, #f3e8ff)',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '4px'
                              }}>
                                {t('lessons.private')}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Lesson Details */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                          {/* Time */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Clock size={14} color="var(--color-text-secondary)" />
                            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                              {lesson.startTime || '--:--'} - {lesson.endTime || '--:--'}
                            </span>
                          </div>

                          {/* Topic */}
                          {lesson.topic && (
                            <div style={{
                              fontSize: '0.8125rem',
                              color: 'var(--color-text-primary)',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {lesson.topic}
                            </div>
                          )}

                          {/* Session Type */}
                          {lesson.sessionType && (
                            <div style={{
                              display: 'inline-block',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              color: 'var(--color-primary-dark)',
                              backgroundColor: 'var(--color-primary-light)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              marginTop: '0.25rem',
                              width: 'fit-content'
                            }}>
                              {getSessionTypeLabel(lesson.sessionType)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => onEdit && onEdit(lesson)}
                          aria-label={t('common.edit')}
                          className="upcoming-lesson-action upcoming-lesson-action--edit"
                          title={t('common.edit')}
                        >
                          <Edit2 size={16} color="var(--color-primary-dark)" style={{ pointerEvents: 'none' }} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete && onDelete(lesson)}
                          aria-label={t('common.delete')}
                          className="upcoming-lesson-action upcoming-lesson-action--delete"
                          title={t('common.delete')}
                        >
                          <Trash2 size={16} color="#ef4444" style={{ pointerEvents: 'none' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{UPCOMING_LESSONS_STYLES}</style>
    </div>
  );
}
